import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, AlertTriangle, CheckCircle, XCircle, Play, Users,
  ChevronRight, Lock, FileText, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getAgents, simulatePoliciesForAgent } from '../services/api';
import { toast } from 'sonner';

const severityColors = {
  critical: '#FF3B30',
  high: '#FF6961',
  medium: '#FFCC00',
  low: '#34C759',
};

const PolicySimulatorPage = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);

  useEffect(() => { fetchAgents(); }, []);

  const fetchAgents = async () => {
    try {
      const resp = await getAgents();
      setAgents(resp.data);
    } catch (err) {
      console.error('Failed to load agents', err);
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async (agentId) => {
    setSimulating(true);
    setSelectedAgent(agentId);
    setSimResult(null);
    try {
      const resp = await simulatePoliciesForAgent(agentId);
      setSimResult(resp.data);
    } catch (err) {
      toast.error('Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-[#9CA3AF]">Loading...</div></div>;
  }

  return (
    <div data-testid="policy-simulator-page" className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <Play className="w-6 h-6 text-[#0066FF]" />
          <h1 className="text-2xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>Policy Simulator</h1>
        </div>
        <p className="text-sm text-[#6B7280]">Evaluate all active policies against any identity — see what would trigger before deployment</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Agent Selector */}
        <div className="enterprise-card p-5">
          <h2 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Select Identity to Simulate</h2>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {agents.map((agent) => {
              const riskColor = severityColors[agent.risk_level] || '#9CA3AF';
              return (
                <button
                  key={agent.id}
                  onClick={() => runSimulation(agent.id)}
                  className={`w-full text-left p-3 transition-colors flex items-center justify-between ${
                    selectedAgent === agent.id ? 'bg-[#0066FF]/10 border border-[#0066FF]/30' : 'bg-[#1A1A1A] hover:bg-[#222] border border-transparent'
                  }`}
                  data-testid={`sim-agent-${agent.id}`}
                >
                  <div>
                    <div className="text-sm font-mono text-[#F9FAFB]">{agent.name}</div>
                    <div className="text-[10px] text-[#6B7280]">{agent.environment} &middot; {agent.type}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold" style={{ color: riskColor }}>{agent.risk_score}</span>
                    <ChevronRight className="w-3 h-3 text-[#6B7280]" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-2">
          {simulating && (
            <div className="enterprise-card p-12 text-center">
              <div className="animate-pulse text-[#0066FF] text-sm">Evaluating policies...</div>
            </div>
          )}

          {simResult && !simulating && (
            <div className="space-y-4" data-testid="sim-results">
              {/* Summary Banner */}
              <div className={`p-5 border ${simResult.can_deploy ? 'border-[#34C759]/30 bg-[#34C759]/5' : 'border-[#FF3B30]/30 bg-[#FF3B30]/5'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {simResult.can_deploy ?
                      <CheckCircle className="w-6 h-6 text-[#34C759]" /> :
                      <XCircle className="w-6 h-6 text-[#FF3B30]" />
                    }
                    <div>
                      <div className={`font-semibold text-base ${simResult.can_deploy ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                        {simResult.can_deploy ? 'DEPLOYMENT ELIGIBLE' : 'DEPLOYMENT BLOCKED'}
                      </div>
                      <div className="text-sm text-[#9CA3AF]">
                        {simResult.agent_name} &middot; {simResult.violations_detected} of {simResult.total_policies} policies triggered
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-mono font-bold text-[#F9FAFB]">{simResult.pass_rate}%</div>
                    <div className="text-[10px] text-[#6B7280] uppercase">Pass Rate</div>
                  </div>
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-3">
                <div className="enterprise-card p-4 text-center">
                  <div className="text-2xl font-mono font-bold text-[#F9FAFB]">{simResult.total_policies}</div>
                  <div className="text-[10px] text-[#6B7280] uppercase">Policies Evaluated</div>
                </div>
                <div className="enterprise-card p-4 text-center">
                  <div className="text-2xl font-mono font-bold text-[#FF3B30]">{simResult.violations_detected}</div>
                  <div className="text-[10px] text-[#6B7280] uppercase">Violations</div>
                </div>
                <div className="enterprise-card p-4 text-center">
                  <div className="text-2xl font-mono font-bold text-[#34C759]">{simResult.total_policies - simResult.violations_detected}</div>
                  <div className="text-[10px] text-[#6B7280] uppercase">Passed</div>
                </div>
              </div>

              {/* Policy Results */}
              <div className="enterprise-card p-5">
                <h3 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Policy Evaluation Details</h3>
                <div className="space-y-2">
                  {simResult.results.map((r, i) => {
                    const sColor = severityColors[r.severity] || '#9CA3AF';
                    return (
                      <div
                        key={i}
                        className={`p-3 border ${r.would_trigger ? 'border-[#FF3B30]/20 bg-[#FF3B30]/5' : 'border-[#27272A]/40 bg-[#1A1A1A]'}`}
                        data-testid={`sim-policy-${r.policy_id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            {r.would_trigger ?
                              <XCircle className="w-4 h-4 text-[#FF3B30]" /> :
                              <CheckCircle className="w-4 h-4 text-[#34C759]" />
                            }
                            <span className="text-sm text-[#F9FAFB] font-medium">{r.policy_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-mono uppercase px-2 py-0.5" style={{ color: sColor, backgroundColor: `${sColor}15` }}>
                              {r.severity}
                            </span>
                            <span className="text-[10px] font-mono text-[#6B7280] uppercase px-2 py-0.5 bg-[#1A1A1A]">
                              {r.action}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-[#9CA3AF] ml-6">{r.reason}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {!simResult && !simulating && (
            <div className="enterprise-card p-12 text-center">
              <Shield className="w-12 h-12 mx-auto mb-4 text-[#27272A]" />
              <p className="text-[#6B7280] text-sm">Select an identity to run policy simulation</p>
              <p className="text-[10px] text-[#6B7280] mt-1">All active policies will be evaluated against the selected agent</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolicySimulatorPage;
